import {
    ActionCreator, AjaxDispatcher
} from './ajax-dispatcher';
import { addClass, addEventListenerIfNotExists, devLog, getApiUrl, getCurrentLanguageId, getPopup, hide, loadAsyncComponent, openComponentInPopup, QueryParameter, removeClass, show } from './common';
import ConfirmDialog from './confirm-dialog';
import HttpClient from './http-client';
import { initCart, initDiscountCoupons, initDiscountCouponsList, stringLocalizer } from './main';
import OrderMatrixLink from './order-matrix-link';
import { PopupOptions, PopupWidth } from './popup';
import ProductCount from './product-count';
import QuantityEdit from './quantity-edit';
import LoadingIndication from './loading-indication';
import SharedCartDialog from './shared-cart-dialog';
import TronCalculator from './tron-calculator';
import { initCartDesignInfo } from './tron-designer/cart-design-info';
import { initTronDesigner, initTronDesignerCartActions, openDesignerForCartCalculation, linkPendingFreeDesignToCart, processPendingPrintJobClones } from './tron-designer/tron-designer';
import { initTronDesignerCartLogoGallery } from './tron-designer/cart-logo-gallery';
import { initCartCollapse } from './cart-collapse';
import { itemFromElement, pushServerAnalyticsEvent, trackRemoveFromCart } from './analytics';

interface UpdateGroupCartItem {
    groupCartItemId: string;
    cartItems: ProductCount[];
}

interface ChangeProductCartItemQuantity {
    cartItemId: string;
    quantity: number;
}

interface CartItem {
    id: string;
    removeButton: HTMLButtonElement;
}

interface ProductCartItem extends CartItem {
    quantityEdit: QuantityEdit;
}

interface GroupCartItem extends CartItem {
    updateButtons: HTMLElement[];
}

interface CalculationCartItem extends GroupCartItem {
    calculationId: string;
}

interface AddPrintingGroupData {
    items: ProductCount[],
    positionId?: string,
    technologyId?: string,
    printDescription: string,
    isSingleProduct: boolean,
    tronLogoGuid: string
}

// Registered once for the page lifetime (the cart can re-init on AJAX reloads; the event is dispatched on
// `document` with bubbles:false, so it must be listened for on `document` directly).
let _printJobSavedListenerBound = false;

export default class Cart {
    private products: ProductCartItem[];
    private groups: GroupCartItem[];
    private calculations: CalculationCartItem[];
    private shareButton: HTMLInputElement;
    private hasErrors = false;

    init(): void {
        const baseElement = document.querySelector('[data-component="Cart"]') as HTMLElement;
        this.hasErrors = baseElement.dataset.hasErrors === 'true';

        this.products = this.createProductCartItems(
            baseElement.querySelectorAll('div[data-cart-item-type="Product"]')
        );
        this.products = this.products.concat(this.createProductCartItems(
            baseElement.querySelectorAll('div[data-cart-item-type="Group_ProductSet"]')
        ));
        this.groups = this.createGroupCartItems(
            baseElement.querySelectorAll('div[data-cart-item-type="Group_ModelVariants"]')
        );

        this.calculations = this.createCalculationCartItems(
            baseElement.querySelectorAll('div[data-cart-item-type="Group_Calculation"]')
        );

        this.registerProductCartItemListeners();
        this.registerGroupCartItemListeners();
        this.registerCalculationCartItemListeners();
        this.registerContinueButtonScrollToError(baseElement);
        this.initStockVerification(baseElement.querySelector('div[data-component="CartStockVerification"]'));
        initTronDesigner();
        initTronDesignerCartActions();
        initTronDesignerCartLogoGallery(() => this.reloadPage());
        initCartCollapse();
        initCartDesignInfo();
        this.initPendingDesignerOpen(baseElement);
        void this.linkPendingFreeDesign();
        void this.clonePendingSharedDesigns();
        this.registerPrintJobSavedRefresh();
        this.registerDeleteDesignButtons();
    }

    // "Delete design" on a print set unlinks its TronDesigner print job (DELETE /api/cart/{id}/printjob)
    // after an explicit confirmation, then refreshes the cart so the group returns to "no design" state.
    private registerDeleteDesignButtons(): void {
        document.querySelectorAll<HTMLButtonElement>('[data-td-delete-design]').forEach(btn => {
            if (btn.dataset.tdDeleteDesignBound) return;
            btn.dataset.tdDeleteDesignBound = '1';

            btn.addEventListener('click', () => {
                const cartItemId = btn.dataset.cartItemId;
                if (!cartItemId) return;

                const confirmDialog = new ConfirmDialog(async () => {
                    const overlayTarget = btn.closest<HTMLElement>('.cart-preview-printing-set') ?? document.body;
                    LoadingIndication.show(overlayTarget);
                    try {
                        // Pass an onError so a failed DELETE resolves (with the non-ok Response, or
                        // undefined on a thrown error) instead of throwing an unhandled rejection out of
                        // this un-awaited ConfirmDialog callback. HttpClient still shows its generic toast.
                        const response = await new HttpClient().fetch(
                            `${this.getApiUrl()}/${encodeURIComponent(cartItemId)}/printjob`,
                            { method: 'DELETE', headers: { 'Current-Language-Id': getCurrentLanguageId() } },
                            () => { /* surfaced by HttpClient; branch on response?.ok below */ }
                        );
                        if (response?.ok) {
                            this.reloadPage();
                        } else {
                            console.error('Failed to delete print design', response?.status);
                        }
                    } finally {
                        LoadingIndication.hide(overlayTarget);
                    }
                });

                void confirmDialog.open([{ key: 'confirmDialogViewName', value: 'ConfirmDeleteDesign' }]);
            });
        });
    }

    // When a design is saved/linked from the cart (Create/Edit Design opens the TD editor in a dialog, so no
    // navigation happens), refresh the cart so it shows the new linked / mismatch state. The save event fires
    // only after the link POST has completed (see handlePrintJobCreated/Updated), so the reloaded cart is current.
    private registerPrintJobSavedRefresh(): void {
        if (_printJobSavedListenerBound) {
            return;
        }
        _printJobSavedListenerBound = true;

        document.addEventListener('tronDesignerPrintJobSaved', async (e: Event) => {
            const cartItemId = (e as CustomEvent).detail?.cartItemId;
            if (cartItemId) {
                // Linked to a cart item (via the editor's customProperties) — just refresh.
                this.reloadPage();
                return;
            }
            // No cart-item id echoed back: a design saved from the cart page wasn't auto-linked. Fall back to
            // linking the pending guid to the single unlinked print set, then refresh. (Genuine free designs
            // are saved on the product-detail page, where this cart listener isn't registered.)
            if (await linkPendingFreeDesignToCart()) {
                this.reloadPage();
            }
        });
    }

    // Shared-cart takeover: clone the sender's print jobs (data-td-pending-clone) into independent
    // copies for this customer and link them, then refresh so the cart shows the linked designs.
    private async clonePendingSharedDesigns(): Promise<void> {
        if (await processPendingPrintJobClones()) {
            this.reloadPage();
        }
    }

    private async linkPendingFreeDesign(): Promise<void> {
        // Design-First: link a free-mode design (saved before the cart item existed) to the new print set,
        // then refresh the cart so it shows the linked state. No-op when there is no pending design.
        const linked = await linkPendingFreeDesignToCart();
        if (linked) {
            this.reloadPage();
        }
    }

    private initPendingDesignerOpen(baseElement: HTMLElement): void {
        if (baseElement.dataset.pendingDesignerOpen !== 'true') {
            devLog("Cart: no pending TronDesigner open");
            return;
        }

        const printSetCartItemId = baseElement.dataset.pendingDesignerPrintSetId;
        if (!printSetCartItemId) {
            devLog("Cart: pending TronDesigner open flag set but printSetCartItemId is missing");
            return;
        }

        devLog("Cart: auto-opening TronDesigner for print set", {
            printSetCartItemId,
            productCartItemId: baseElement.dataset.pendingDesignerProductId,
            calculationGuid: baseElement.dataset.pendingDesignerCalculationId,
            printJobGuid: baseElement.dataset.pendingDesignerPrintJobGuid,
        });

        void openDesignerForCartCalculation(printSetCartItemId);
    }
    private async initStockVerification(element: HTMLElement): Promise<void> {
        if (!element) return;
        const loadedElement = await loadAsyncComponent(element);
        const isStockValid = loadedElement.dataset.stockValid === 'true';
        isStockValid && !this.hasErrors
            ? this.enableContinue(loadedElement.dataset.continueUrl)
            : this.disableContinue();

        const icon = this.getBaseElement().querySelector('[data-component-part="continue-loading-icon"]') as HTMLElement;
        icon && hide(icon);
        const label = this.getBaseElement().querySelector('[data-component-part="continue-button-label"]') as HTMLElement;
        label && show(label);

        isStockValid && await this.reloadTabNavigation();
    }
    private async reloadTabNavigation(): Promise<void> {
        const tabNavigation = this.getBaseElement().querySelector('[data-component="TabNavigation"]') as HTMLElement;
        await tabNavigation && loadAsyncComponent(tabNavigation);
    }
        enableContinue(url: string) {
        const baseElement = this.getBaseElement();
        const continueButton = baseElement.querySelector('[data-component-part="continue-button"]') as HTMLAnchorElement;
        continueButton.removeAttribute('data-disabled');
        if (url) {
            continueButton.href = url;
        }
        removeClass(continueButton, 'disabled');
    }
    private disableContinue(): void {
        const baseElement = this.getBaseElement();
        const continueButton = baseElement.querySelector('[data-component-part="continue-button"]') as HTMLAnchorElement;
        continueButton.setAttribute('data-disabled', 'true');
        const url = continueButton.href;
        if (url) {
            continueButton.removeAttribute('href');
        }
        addClass(continueButton, 'disabled');
    }

    private registerContinueButtonScrollToError(baseElement: HTMLElement): void {
        const continueButton = baseElement.querySelector('[data-component-part="continue-button"]') as HTMLElement;
        if (!continueButton) return;

        addEventListenerIfNotExists(continueButton, 'click', () => {
            const firstError = baseElement.querySelector('.cart-message--error');
            if (firstError) {
                const element = firstError as HTMLElement;
                element.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
        });
    }

    initForCartSharing(shareButtonId: string): void {
        this.initShareCartElements(shareButtonId);
        this.registerShareCartListeners();
    }

    private initShareCartElements(shareButtonId: string): void {
        this.shareButton = document.getElementById(shareButtonId) as HTMLInputElement;
    }

    private registerShareCartListeners(): void {
        // The empty cart (EmptyCart.cshtml) runs initCart too but has no share button; a throw here would stop the
        // data-ts-script scan, so later components on the page would stay uninitialised.
        if (!this.shareButton) return;

        addEventListenerIfNotExists(this.shareButton, 'click', async () => {
            await this.openShareConfirmDialog();
        });
    }

    private async openShareConfirmDialog(): Promise<void> {
        const confirmDialog = new ConfirmDialog(async () => {
            await this.shareCart();
        });

        const queryParam: QueryParameter = {
            key: 'confirmDialogViewName',
            value: 'ConfirmShareCart'
        };

        confirmDialog.open([queryParam]);
    }

    private async shareCart(): Promise<void> {

        const url = `${this.getSharedCartApiUrl()}/create`;
        const languageId = getCurrentLanguageId();
        const response = await new HttpClient().fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Current-Language-Id': languageId
            },
        });

        if (response && response.ok) {
            this.openShareDialog(await response.text());
        }
    }

    private async openShareDialog(sharedCartGuid: string): Promise<void> {
        const queryParam: QueryParameter = {
            key: 'guid',
            value: `${sharedCartGuid}`
        };
        await openComponentInPopup('SharedCartDialog', [queryParam], { id: 'share-cart-dialog', width: PopupWidth.Small });

        const dialog = new SharedCartDialog();
        dialog.init("copySharedCartUrlButton", "sharedCartUrlInput");
    }

    async addProduct(productCounts: ProductCount[]): Promise<void> {
        if (!this.validate(productCounts)) {
            return;
        }

        if (productCounts.length === 1) {
            await this.addProductToCart(productCounts[0]);
        }
        else {
            await this.addProductsToCart(productCounts);
        }
    }

    async addSingleProductPrintingGroup(productCount: ProductCount, demandText: string, positionId: string = null, technologyId: string = null): Promise<void> {
        const requestData: AddPrintingGroupData = {
            items: [ productCount ],
            positionId: positionId,
            technologyId: technologyId,
            printDescription: demandText,
            isSingleProduct: true,
            tronLogoGuid: this.getTronLogoGuid()
        };
        await this.addPritingGroupInternal(requestData);
    }

    private getTronLogoGuid(): string {
        return window?.tron?.User?.getUserLogo() ?? null;
    }

    async addPrintingGroup(productCounts: ProductCount[], demandText: string, positionId: string = null, technologyId: string = null): Promise<void> {
        if (!this.validate(productCounts)) return;
        const requestData: AddPrintingGroupData = {
            items: productCounts,
            positionId: positionId,
            technologyId: technologyId,
            printDescription: demandText,
            isSingleProduct: false,
            tronLogoGuid: this.getTronLogoGuid()
        };
        await this.addPritingGroupInternal(requestData);
    }

    private async addPritingGroupInternal(requestData: AddPrintingGroupData){
        const response = await new HttpClient().fetch(`/api/cart/printing`, {
            method: 'POST',
            body: JSON.stringify(requestData),
            headers: {
                'Content-Type': 'application/json',
                'Current-Language-Id': getCurrentLanguageId()
            }
        });

        if (response.ok) {
            this.reloadCartHeaderIcon();
            await this.pushAddToCartAnalytics(response);
        }
    }

    /**
     * The add endpoints answer {"analytics": <add_to_cart event> | null} built from the cart's own lines
     * (real unit prices, variants, categories) — see CartController.AddedAsync. Nothing on the page is scraped.
     */
    private async pushAddToCartAnalytics(response: Response): Promise<void> {
        await this.pushCartAnalytics(response);
    }

    /**
     * Cart API answers carry {"analytics": <event> | [<event>, …] | null}: add_to_cart from the add endpoints,
     * add_to_cart / remove_from_cart for quantity changes and removals (CartController). Returns false when no
     * event came (an older server, or the server could not build one), so the caller can fall back.
     */
    private async pushCartAnalytics(response: Response): Promise<boolean> {
        try {
            const body = await response.json();
            if (!body || !('analytics' in body)) {
                return false;
            }
            const events = (Array.isArray(body.analytics) ? body.analytics : [body.analytics])
                .filter((event: unknown) => typeof event === 'object' && event !== null && typeof (event as { event?: unknown }).event === 'string');
            events.forEach((event: unknown) => pushServerAnalyticsEvent(event));
            return events.length > 0;
        } catch {
            // Empty / non-JSON body: the cart change succeeded, only the event is skipped.
            return false;
        }
    }

    public getBaseElement(): HTMLElement {
        return document.querySelector('[data-component="Cart"]');
    }

    private validate(productCounts: ProductCount[]): boolean {
        if (productCounts.length === 0) return false;

        const productsWithZeroQuantity = productCounts.filter(p => p.quantity === 0);
        if(productsWithZeroQuantity.length === 0) return true;
    }

    private async addProductToCart(productCount: ProductCount): Promise<void> {
        const response = await new HttpClient().fetch(`/api/cart/product`, {
            method: 'POST',
            body: JSON.stringify(productCount),
            headers: {
                'Content-Type': 'application/json',
                'Current-Language-Id': getCurrentLanguageId()
            }
        });

        if (response.ok) {
            this.reloadCartHeaderIcon();
            await this.pushAddToCartAnalytics(response);
        }
    }

    private async addProductsToCart(productCounts: ProductCount[]): Promise<void> {
        const response = await new HttpClient().fetch(`/api/cart/products`, {
            method: 'POST',
            body: JSON.stringify(productCounts),
            headers: {
                'Content-Type': 'application/json',
                'Current-Language-Id': getCurrentLanguageId()
            }
        });

        if (response.ok) {
            this.reloadCartHeaderIcon();
            await this.pushAddToCartAnalytics(response);
        }
    }

    public async openCartDialog(productCounts: ProductCount[]): Promise<void> {
        const queryParam: QueryParameter = {
            key: 'product-counts',
            value: this.buildProductCountQueryString(productCounts)
        };
        await openComponentInPopup('CartDialog', [queryParam], { id: 'product-added-to-cart-popup', width: PopupWidth.Large }, true);
    }

    private buildProductCountQueryString(productCounts: ProductCount[]): string {
        return productCounts.map(i => i.productId + '-' + i.quantity).join(';');
    }

    private registerProductCartItemListeners(): void {
        this.registerRemoveItemListener(this.products);
    }

    private registerGroupCartItemListeners(): void {
        this.registerRemoveItemListener(this.groups);
        this.initOrderMatrixLinks();
    }

    private registerCalculationCartItemListeners(): void {
        this.registerRemoveItemListener(this.calculations);
        this.calculations.forEach(c => 
            c.updateButtons.forEach(b => b.addEventListener('click', async () => this.enterCalculator(c.calculationId)))
        );
    }

    private registerRemoveItemListener(cartItems: CartItem[]): void {
        cartItems.forEach(item => {
            item.removeButton &&
                addEventListenerIfNotExists(
                    item.removeButton,
                    'click',
                    async () => await this.removeCartItem(item.id)
                );
        });
    }

    private initOrderMatrixLinks(): void {
        this.groups.forEach(group => group.updateButtons.forEach(async b => {
            const link = new OrderMatrixLink(
                b,
                async (productCounts: ProductCount[]) => {
                    await this.updateGroupCartItem({
                        groupCartItemId: group.id,
                        cartItems: productCounts
                    });
                },
                null
            );

            await link.init(group.id);
        }));
    }

    private async enterCalculator(calculationGuid: string): Promise<void> {
        const calculator = new TronCalculator();
        await calculator.openExistingCalculation(calculationGuid);
    }

    private createProductCartItems(inputElements: NodeList): ProductCartItem[] {
        const array: HTMLInputElement[] = Array.prototype.slice.call(
            inputElements
        );

        const productCartItems: ProductCartItem[] = array.map(item => {
            return {
                id: item.getAttribute('data-product-id'),
                quantityEdit: new QuantityEdit(item.querySelector('[data-component-part="quantity-edit"]'), 
                    number => this.handleChangeQuantity(item.getAttribute('data-product-id'), number)),
                removeButton: item.querySelector('[data-action="remove"]')
            } as ProductCartItem;
        });

        return productCartItems;
    }

    private createGroupCartItems(inputElements: NodeList) {
        const array: HTMLInputElement[] = Array.prototype.slice.call(
            inputElements
        );

        return array.map(item => {
            return {
                id: item.getAttribute('data-product-id'),
                removeButton: item.querySelector('[data-action="remove"]'),
                updateButtons: Array.prototype.slice.call(item.querySelectorAll(
                    '[data-action="change-quantity"]'
                )).map(x => x as HTMLElement)
            } as GroupCartItem;
        });
    }

    private createCalculationCartItems(inputElements: NodeList) {
        const array: HTMLInputElement[] = Array.prototype.slice.call(
            inputElements
        );

        return array.map(item => {
            return {
                id: item.getAttribute('data-product-id'),
                calculationId: item.getAttribute('data-calculation-id'),
                removeButton: item.querySelector('[data-action="remove"]'),
                updateButtons: Array.prototype.slice.call(item.querySelectorAll(
                    '[data-action="change-quantity"]'
                )).map(x => x as HTMLElement)
            } as CalculationCartItem;
        });
    }

    private async handleChangeQuantity(
        cartItemId: string,
        quantity: number
    ): Promise<void> {
        quantity > 0 
            ? await this.changeProductQuantity({ cartItemId, quantity })
            : new ConfirmDialog(
                    async () => await this.removeCartItem(cartItemId),
                    () => this.reloadPage())
                .open();
        
    }

    private async changeProductQuantity(
        changeProductQuantityData: ChangeProductCartItemQuantity
    ): Promise<void> {
        const url = `${this.getApiUrl()}/product`;
        const languageId = getCurrentLanguageId();
        const response = await new HttpClient().fetch(url, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
                'Current-Language-Id': languageId
            },
            body: JSON.stringify(changeProductQuantityData)
        });

        if (response && response.ok) {
            await this.pushCartAnalytics(response);
            this.reloadPage();
        }
    }

    private async updateGroupCartItem(
        updateGroup: UpdateGroupCartItem
    ): Promise<void> {
        const url = '/api/cart/products';
        const response = await new HttpClient().fetch(url, {
            method: 'PUT',
            body: JSON.stringify(updateGroup),
            headers: {
                'Content-Type': 'application/json',
                'Current-Language-Id': getCurrentLanguageId()
            }
        });

        if (response.ok) {
            await this.pushCartAnalytics(response);
            this.reloadPage();
            this.closePopup();
        }
    }

    private closePopup(): void {
        const popup = getPopup(<PopupOptions>{ id: 'order-matrix-popup' });
        popup.close();
    }

    private async removeCartItem(itemId: string): Promise<void> {
        const row = document.querySelector(`[data-component="Cart"] [data-product-id="${itemId}"]`);
        const item = itemFromElement(row);

        const url = `${this.getApiUrl()}?cartItemId=${itemId}`;
        const response = await new HttpClient().fetch(url, {
            method: 'DELETE',
            headers: {
                'Content-Type': 'application/json',
                'Current-Language-Id': getCurrentLanguageId()
            }
        });

        if (response.ok) {
            // The server event has the cart line's own shape (item_variant, model item_id); the card's data-ga-*
            // attributes are only the fallback for a server that sends none.
            if (!await this.pushCartAnalytics(response) && item) {
                trackRemoveFromCart([item]);
            }
            this.reloadPage();
        }
    }

    private reloadCartHeaderIcon(): void {
        const action = new ActionCreator().reloadComponent('CartHeaderIcon');
        new AjaxDispatcher().dispatchGetAction(action);
    }

    public reloadPage(): void {

        const action = new ActionCreator().reloadComponents([
            'Cart',
            'CartHeaderIcon',
            'DiscountCoupons',
            'DiscountCouponsList'
        ]);
        // action.sucessAction = () => {
        //     initCart();
        //     initDiscountCoupons();
        //     initDiscountCouponsList();
        // };
        action.successAction = () => this.announceCartUpdate();

        new AjaxDispatcher().dispatchGetAction(action);
    }

    // A persistent, screen-reader-only live region kept on <body> so the cart's AJAX component swap
    // (which replaceWith()s the whole Cart section) can't destroy it between announcements.
    private ensureLiveRegion(): HTMLElement {
        let region = document.getElementById('cart-live-region');
        if (!region) {
            region = document.createElement('div');
            region.id = 'cart-live-region';
            region.setAttribute('role', 'status');
            region.setAttribute('aria-live', 'polite');
            region.setAttribute('aria-atomic', 'true');
            // Self-contained visually-hidden styling — no dependency on a utility class being present.
            region.style.cssText = 'position:absolute;width:1px;height:1px;margin:-1px;padding:0;overflow:hidden;clip:rect(0 0 0 0);white-space:nowrap;border:0;';
            document.body.appendChild(region);
        }
        return region;
    }

    // After a cart reload, tell assistive tech the cart changed. The preview page has no single grand
    // total to read out (the summary lives on the delivery step), and the visible per-item totals just
    // re-rendered — so a short polite "cart updated" prompts the user to re-read rather than reciting a
    // possibly-ambiguous number.
    private announceCartUpdate(): void {
        const region = this.ensureLiveRegion();
        const message = stringLocalizer?.get('CartUpdated');
        const text = message && !message.startsWith('Missing.') ? message : 'Cart updated';
        // Clear first so an identical consecutive message is still re-announced.
        region.textContent = '';
        window.setTimeout(() => { region.textContent = text; }, 60);
    }

    private getApiUrl(): string {
        return `${getApiUrl()}/cart`;
    }

    private getSharedCartApiUrl(): string {
        return `${getApiUrl()}/sharedcart`;
    }
}
